import { TextTooltip } from '@ui/components/TextTooltip';
import { usePairMarketScheduleStore } from '@ui/features/RWA/hooks/usePairMarketScheduleStore/usePairMarketScheduleStore';
import { useClosingTradesStore } from '@ui/features/Websocket/useClosingTradesStore';
import { useDataChainId } from '@ui/hooks/datachain';
import { Button } from '@ui/lib/park/ui/button';
import { Link } from '@ui/lib/park/ui/link';
import { Text } from '@ui/lib/park/ui/text';
import { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV2/types';
import { useTranslation } from 'next-i18next';
import { useEffect, useMemo, useState } from 'react';
import { VStack } from 'styled-system/jsx';

import { useClosingPositionsStore } from '../../store/useClosingPositionsStore';
import { ClosePositionOverlay } from '../ClosePosition/ClosePositionOverlay';

type PositionWithEarliestCloseTime = Position & {
  earliestCloseTime?: number;
};

// TODO: update to the final external docs link
const MIN_TRADE_DURATION_LEARN_MORE_URL = 'https://leverup.gitbook.io/docs/trading/holding-trades#minimum-trade-duration';
const POSITION_CLOSE_BUTTON_DESKTOP_WIDTH = '52px';

const toTimestampMs = (timestamp?: number) => {
  if (!timestamp) return undefined;

  return timestamp > 1_000_000_000_000 ? timestamp : timestamp * 1000;
};

export const PositionCloseButton = ({
  position,
}: {
  position: PositionWithEarliestCloseTime;
}) => {
  const { t } = useTranslation();
  const closingPositionIds = useClosingPositionsStore(
    (s) => s.closingPositionIds,
  );
  const closingTrades = useClosingTradesStore((s) => s.useClosingTrades());
  const chainId = useDataChainId();
  const isMarketHourOpen = usePairMarketScheduleStore(
    (s) =>
      s.getMarketScheduleByPair(position.pair.pairName, chainId).isClientOpen,
  );

  const isPositionClosing =
    closingPositionIds.some(
      (closingPosition) =>
        closingPosition.id === position.id &&
        (closingPosition.positionTimestamp === undefined ||
          closingPosition.positionTimestamp === position.timestamp),
    ) &&
    closingTrades.some(
      (closingTrade) =>
        closingTrade.positionHash === position.id &&
        (closingTrade.positionTimestamp !== undefined
          ? closingTrade.positionTimestamp === position.timestamp
          : (toTimestampMs(position.timestamp) ?? 0) <=
            closingTrade.submittedAt),
    );

  const earliestCloseTime = position.earliestCloseTime ?? 0; // unix seconds
  const [nowMs, setNowMs] = useState(() => Date.now());
  const nowSec = Math.floor(nowMs / 1000);
  const remainingSec =
    earliestCloseTime > 0 ? Math.max(0, earliestCloseTime - nowSec) : 0;
  const isInEarliestCloseCountdown = remainingSec > 0;

  useEffect(() => {
    if (!isInEarliestCloseCountdown) return;
    const timer = setInterval(() => setNowMs(Date.now()), 1000);
    
return () => clearInterval(timer);
  }, [isInEarliestCloseCountdown]);

  const countdownText = useMemo(() => {
    const min = Math.floor(remainingSec / 60);
    const sec = remainingSec % 60;
    
return `${min}:${String(sec).padStart(2, '0')}`;
  }, [remainingSec]);

  const isDisabled =
    !isMarketHourOpen ||
    isPositionClosing ||
    position.isPendingPosition ||
    isInEarliestCloseCountdown;

  const buttonEl = (
    <Button
      disabled={isDisabled}
      w={{
        base: 'full',
        desktop: POSITION_CLOSE_BUTTON_DESKTOP_WIDTH,
      }}
      // V2 compact row button on desktop (24–28px), full-size in mobile cards
      h={{ desktop: '26px' }}
      px={{ desktop: '10px' }}
      py={{ desktop: '0' }}
      variant="secondary"
      css={{
        // avoid width jitter while the countdown number changes
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {isPositionClosing
        ? t('common.Closing')
        : isInEarliestCloseCountdown
          ? countdownText
          : t('common.Close')}
    </Button>
  );

  // During minimum trade duration, the button is disabled.
  // We intentionally don't render the dialog trigger so it can't be opened,
  // and use a wrapper element so tooltip still works on hover.
  if (isInEarliestCloseCountdown) {
    return (
      <TextTooltip
        content={
          <VStack gap={2} alignItems="flex-start" p={2}>
            <VStack gap={1} alignItems="flex-start">
              <Text textStyle="text.small">1. Trade duration based on Size</Text>
              <Text textStyle="text.small">
                2. No TP during minimum trade duration
              </Text>
            </VStack>
            <Link
              href={MIN_TRADE_DURATION_LEARN_MORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              textStyle="text.small"
              textDecoration="underline"
            >
              Learn More
            </Link>
          </VStack>
        }
        positioning={{ placement: 'top' }}
        closeDelay={500}
      >
        <span style={{ display: 'block', cursor: 'help' }}>{buttonEl}</span>
      </TextTooltip>
    );
  }

  return (
    <ClosePositionOverlay position={position}>
      {buttonEl}
    </ClosePositionOverlay>
  );
};
